import React, { useState, useEffect, useRef, useImperativeHandle, useCallback, useMemo } from 'react';
import './formulaire-dynamique.css';
import Translator from "../translator";
import withNavigation from '../higher-order/navigation';
import Stepper from '../stepper/Stepper';
import { validateFieldValue, FormulaireTypeChamp, isLabelOrTitleField } from './form-inputs/utils';
import { fillAllRequiredFields } from './form-test-utils';
// import FormTestButton from './form-test-button';
import { useScrollRestoration } from './custom-steps/hooks/useScrollRestoration';
import {
  LabelInput,
  BoldLabelInput,
  BoldTitleInput,
  TextInput,
  MemoInput,
  NumericInput,
  DecimalInput,
  CheckBoxInput,
  DateInput,
  DateTimeInput,
  RadioGroupInput,
  CheckedListBoxInput,
  DropDownListInput,
  PhoneInput,
  ExpirationMonthYearInput
} from './form-inputs';

const getDefaultValue = (field) => {
  switch (field.typeChamp) {
    case FormulaireTypeChamp.CheckBox:
    case FormulaireTypeChamp.CheckedListBox:
      return [];
    case FormulaireTypeChamp.RadioGroup:
      return '';
    case FormulaireTypeChamp.Numeric:
    case FormulaireTypeChamp.Decimal:
      return 0;
    case FormulaireTypeChamp.Date:
    case FormulaireTypeChamp.DateNaissance:
    case FormulaireTypeChamp.DateTime:
    case FormulaireTypeChamp.Phone:
      return '';
    case FormulaireTypeChamp.ExpirationMonthYear:
      return '';
    default:
      return field.valeurDefaut || '';
  }
};

const STEPPER_CONFIG = {
  activeColor: "var(--lighter-primary-color)",
  completeColor: "var(--primary-color)",
  circleFontSize: 18,
  titleFontSize: 18
};

/**
 * Scrolls the first error field into view. Expects DOM elements with data-field-id="<fieldId>".
 * Uses a short delay so React has committed validation state and the target is in the DOM.
 * For multi-step or tabbed forms, can switch to the step that contains the first error before scrolling.
 *
 * @param {Object} errors - Map of fieldId -> error message
 * @param {Object} [options] - Optional step-switching for tabbed/multi-step forms
 * @param {function(string): number|null} [options.getStepIndexFromFieldId] - Returns step index for a field id, or null
 * @param {number} [options.currentStep] - Current step index
 * @param {function(number): void} [options.setCurrentStep] - Callback to switch step
 */
export function scrollToFirstValidationError(errors, options = {}) {
  const firstErrorFieldId = Object.keys(errors || {})[0];
  if (!firstErrorFieldId) return;

  const { getStepIndexFromFieldId, currentStep, setCurrentStep } = options;
  let delay = 150;
  if (typeof getStepIndexFromFieldId === 'function' && setCurrentStep && currentStep !== undefined) {
    const stepIndex = getStepIndexFromFieldId(firstErrorFieldId);
    if (stepIndex != null && stepIndex !== currentStep) {
      setCurrentStep(stepIndex);
      delay = 350;
    }
  }

  setTimeout(() => {
    const el = document.querySelector(`[data-field-id="${firstErrorFieldId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, delay);
}

const FormulaireDynamique = React.forwardRef((props, ref) => {
  const { fields, onChange, sectionsHorizontales, nombreColonnesSections } = props;
  const hideSubmitButton = props.hideSubmitButton === true;
  const language = props.language || props.match?.params?.language || 'fr';

  // Check if fields is a multi-step form (array of arrays
  // or array with custom component objects)
  const isMultiStep = useMemo(() => {
    if (!Array.isArray(fields) || fields.length === 0) {
      return false;
    }
    const firstStep = fields[0];
    return Array.isArray(firstStep) || (typeof firstStep === 'object' && firstStep !== null && firstStep.customComponent);
  }, [fields]);

  const currentFields = fields || [];

  const [currentStep, setCurrentStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);

  const getFieldsToProcess = useCallback(() => {
    return isMultiStep ? fields.flat() : fields;
  }, [fields, isMultiStep]);

  const extractFormDataFromFields = useCallback((includeDefaults = false) => {
    if (!fields || fields.length === 0) {
      return {};
    }

    const data = {};
    const fieldsToProcess = getFieldsToProcess();

    fieldsToProcess.forEach(field => {
      if (field && typeof field === 'object' && field.typeChamp !== undefined) {
        if (!isLabelOrTitleField(field)) {
          if (field.value !== undefined) {
            data[field.idFormulaireDetail] = field.value;
          } else if (includeDefaults) {
            data[field.idFormulaireDetail] = getDefaultValue(field);
          }
        }
      }
    });
    return data;
  }, [fields, isMultiStep, getFieldsToProcess]);

  const [formData, setFormData] = useState(() => extractFormDataFromFields(true));
  const [errors, setErrors] = useState({});

  const enableScrollRestoration = props.enableScrollRestoration !== false;
  const scrollKey = props.match?.url || props.match?.path || undefined;
  useScrollRestoration(
    enableScrollRestoration
      ? { storageKey: scrollKey }
      : { storageKey: scrollKey, ready: false }
  );

  useEffect(() => {
    const newData = extractFormDataFromFields(false);
    if (Object.keys(newData).length > 0) {
      setFormData(prevData => ({ ...prevData, ...newData }));
    }
  }, [extractFormDataFromFields]);

  const onChangeField = useCallback((fieldId, value) => {
    setFormData(prevData => {
      const newData = { ...prevData, [fieldId]: value };

      if (onChange) {
        onChange(newData);
      }

      return newData;
    });

    setErrors(prevErrors => ({
      ...prevErrors,
      [fieldId]: '' // Clear error when user starts typing
    }));
  }, [onChange]);


  const validateField = useCallback((field, value, errors) => {
    if (!field || typeof field !== 'object' || field.typeChamp === undefined || !field.idFormulaireDetail) {
      return false;
    }
    const fieldId = field.idFormulaireDetail;
    const fieldError = validateFieldValue(field, value, language);

    if (fieldError) {
      errors[fieldId] = fieldError;
      return true;
    }

    return false;
  }, [language]);

  const validateFieldsArray = useCallback((fieldsArray, errors) => {
    let isValid = true;
    fieldsArray.forEach(field => {
      const fieldId = field?.idFormulaireDetail;
      if (!fieldId) {
        return;
      }

      const value = formData[fieldId];
      const hasError = validateField(field, value, errors);
      if (hasError) {
        isValid = false;
      }
    });
    return isValid;
  }, [formData, validateField]);

  const validateStep = useCallback((stepIndex) => {
    const step = currentFields[stepIndex];
    if (!step) {
      return { isValid: true, errors: {} };
    }

    // Custom component step - call the component's own validate method
    if (step.customComponent) {
      // Check if the custom component has a static validate method
      if (typeof step.customComponent.validate === 'function') {
        return step.customComponent.validate(formData, language);
      }
      // If no validate method, assume valid
      return { isValid: true, errors: {} };
    }

    const stepFields = Array.isArray(step) ? step : [];
    if (stepFields.length === 0) {
      return { isValid: true, errors: {} };
    }

    const stepValidationErrors = {};
    const stepIsValid = validateFieldsArray(stepFields, stepValidationErrors);

    return { isValid: stepIsValid, errors: stepValidationErrors };
  }, [currentFields, formData, language, validateFieldsArray]);

  const validate = useCallback((fieldsToValidate = null, validateAllSteps = false) => {
    if (validateAllSteps && isMultiStep) {
      const allErrors = {};
      let allValid = true;
      let firstErrorStepIndex = -1;

      // Validate all steps and collect errors
      for (let i = 0; i < currentFields.length; i++) {
        const stepValidation = validateStep(i);
        if (!stepValidation.isValid) {
          allValid = false;
          Object.assign(allErrors, stepValidation.errors);
          // Track the first step with errors for navigation
          if (firstErrorStepIndex === -1) {
            firstErrorStepIndex = i;
          }
        }
      }

      // Navigate to the first step with errors if any
      if (!allValid && firstErrorStepIndex !== -1) {
        setCurrentStep(firstErrorStepIndex);
      }

      setErrors(allErrors);
      return { isValid: allValid, errors: allErrors };
    }

    // Skip la validation pour les étapes customisées (lors de la navigation normale)
    // Elles gèrent leur propre validation
    if (isMultiStep && currentFields[currentStep]?.customComponent) {
      return { isValid: true, errors: {} };
    }

    const fieldsForValidation = fieldsToValidate || (isMultiStep && currentFields[currentStep] ? currentFields[currentStep] : currentFields);

    if (!fieldsForValidation || fieldsForValidation.length === 0) {
      return { isValid: true, errors: {} };
    }

    const validationErrors = {};
    const isValid = validateFieldsArray(
      Array.isArray(fieldsForValidation) ? fieldsForValidation : [],
      validationErrors
    );

    setErrors(validationErrors);
    return { isValid, errors: validationErrors };
  }, [currentFields, currentStep, isMultiStep, validateStep, validateFieldsArray]);

  useImperativeHandle(ref, () => ({
    validate,
    getFormData: () => formData
  }), [validate, formData]);

  const renderField = useCallback((field) => {
    const fieldId = field.idFormulaireDetail;
    const value = formData[fieldId] !== undefined ? formData[fieldId] : getDefaultValue(field);
    const error = errors[fieldId];
    const isRequired = field.requis || false;

    const NOM_CHAMP = language === 'fr'
      ? (field.descriptionFrancaise || field.label || '')
      : (field.descriptionAnglaise || field.descriptionFrancaise || field.label || '');
    const PLACEHOLDER_CHAMP = (field.placeholder || '');

    const commonProps = {
      field,
      fieldId,
      value,
      onChange: onChangeField,
      label: NOM_CHAMP,
      isRequired,
      error,
      placeholder: PLACEHOLDER_CHAMP,
      language
    };

    switch (field.typeChamp) {
      case FormulaireTypeChamp.Label:
        return <LabelInput field={field} label={NOM_CHAMP} />;

      case FormulaireTypeChamp.BoldLabel:
        return <BoldLabelInput field={field} label={NOM_CHAMP} />;

      case FormulaireTypeChamp.BoldTitle:
        return <BoldTitleInput field={field} label={NOM_CHAMP} />;

      case FormulaireTypeChamp.Text:
        return <TextInput {...commonProps} />;

      case FormulaireTypeChamp.Memo:
        return <MemoInput {...commonProps} />;

      case FormulaireTypeChamp.Numeric:
        return <NumericInput {...commonProps} />;

      case FormulaireTypeChamp.Decimal:
        return <DecimalInput {...commonProps} />;

      case FormulaireTypeChamp.CheckBox:
        return <CheckBoxInput {...commonProps} />;

      case FormulaireTypeChamp.Date:
      case FormulaireTypeChamp.DateNaissance:
        return <DateInput {...commonProps} />;

      case FormulaireTypeChamp.DateTime:
        return <DateTimeInput {...commonProps} />;

      case FormulaireTypeChamp.RadioGroup:
        return <RadioGroupInput {...commonProps} />;

      case FormulaireTypeChamp.CheckedListBox:
        return <CheckedListBoxInput {...commonProps} />;

      case FormulaireTypeChamp.DropDownList:
        return <DropDownListInput {...commonProps} />;

      case FormulaireTypeChamp.Phone:
        return <PhoneInput {...commonProps} />;

      case FormulaireTypeChamp.ExpirationMonthYear:
        return <ExpirationMonthYearInput {...commonProps} />;

      default:
        return null;
    }
  }, [formData, errors, onChangeField, language]);

  const fieldsToRender = useMemo(() => {
    if (!fields || fields.length === 0) {
      return [];
    }
    if (isMultiStep) {
      const step = currentFields[currentStep];
      return Array.isArray(step) ? step : [];
    }
    return currentFields;
  }, [isMultiStep, currentFields, currentStep]);

  const groupFieldsIntoSections = useMemo(() => {
    const fieldsToGroup = fieldsToRender;
    if (!fieldsToGroup || fieldsToGroup.length === 0) {
      return { mainTitle: null, sections: [] };
    }

    const sections = [];
    let currentSection = null;
    let mainTitle = null;
    let isFirstBoldTitle = true;

    fieldsToGroup.forEach((field, index) => {
      // BoldTitle: first one is main title, subsequent ones are section headers
      if (field.typeChamp === FormulaireTypeChamp.BoldTitle) {
        if (isFirstBoldTitle) {
          mainTitle = field;
          isFirstBoldTitle = false;
          return; // Skip adding first BoldTitle to sections
        }
        // Subsequent BoldTitle fields act as section headers
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          header: field,
          fields: [],
          colonnes: field.colonnes || 1,
          pleineLargeur: field.pleineLargeur || false
        };
        return;
      }

      // BoldLabel starts a new section
      if (field.typeChamp === FormulaireTypeChamp.BoldLabel) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = {
          header: field,
          fields: [],
          colonnes: field.colonnes || 1,
          pleineLargeur: field.pleineLargeur || false
        };
      } else {
        // Add field to current section or create a default section
        if (!currentSection) {
          currentSection = {
            header: null,
            fields: [],
            colonnes: 1
          };
        }
        currentSection.fields.push(field);
      }
    });

    if (currentSection) {
      sections.push(currentSection);
    }

    return { mainTitle, sections };
  }, [fieldsToRender]);

  // const handleFillAllRequiredFields = useCallback(() => {
  //   if (fieldsToRender.length === 0) {
  //     return;
  //   }
  //   const handleFormChange = (newFormData) => {
  //     if (typeof newFormData === 'object' && newFormData !== null) {
  //       Object.keys(newFormData).forEach(fieldId => {
  //         onChangeField(fieldId, newFormData[fieldId]);
  //       });
  //     }
  //   };
  //   fillAllRequiredFields(fieldsToRender, handleFormChange, language);
  // }, [fieldsToRender, onChangeField, language]);

  const stepperSteps = useMemo(() => {
    if (!isMultiStep || !currentFields) {
      return [];
    }
    return currentFields.map((stepData, index) => {
      // Mock step
      if (stepData?.mockStep) {
        return { id: index, title: stepData.title || `Étape ${index + 1}` };
      }
      // Custom component step
      if (stepData?.customComponent) {
        return { id: index, title: stepData.title || `Étape ${index + 1}` };
      }
      // Regular step with fields
      const stepFields = Array.isArray(stepData) ? stepData : [];
      const titleField = stepFields?.find(f => f && f.typeChamp === FormulaireTypeChamp.BoldTitle);
      return {
        id: index,
        title: titleField
          ? (language === 'fr'
            ? (titleField.descriptionFrancaise || `Étape ${index + 1}`)
            : (titleField.descriptionAnglaise || titleField.descriptionFrancaise || `Step ${index + 1}`))
          : (language === 'fr' ? `Étape ${index + 1}` : `Step ${index + 1}`)
      };
    }).filter(step => step !== null);
  }, [isMultiStep, currentFields, language]);

  // Step navigation handlers
  const handleStepChange = useCallback((stepId) => {
    if (stepId < 0 || stepId >= currentFields.length) {
      return;
    }

    if (stepId > currentStep) {
      const validation = validate();
      if (!validation.isValid) {
        return;
      }
      // If max step reached
      if (stepId > maxStepReached) {
        setMaxStepReached(stepId);
      }
    }

    setCurrentStep(stepId);
    // Effacer les erreurs lors du changement d'étape
    setErrors({});
  }, [currentStep, currentFields, maxStepReached, validate]);

  const goToNextStep = useCallback(() => {
    if (currentStep < currentFields.length - 1) {
      const validation = validate();
      if (validation.isValid) {
        const nextStep = currentStep + 1;
        if (nextStep > maxStepReached) {
          setMaxStepReached(nextStep);
        }
        setCurrentStep(nextStep);
        setErrors({});
      }
    }
  }, [currentStep, currentFields, maxStepReached, validate]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  }, [currentStep]);

  const resetStepperAndGoToFirstStep = useCallback(() => {
    setMaxStepReached(0);
    setCurrentStep(0);
    setErrors({});
  }, []);

  const renderStepper = useMemo(() => {
    if (!isMultiStep || stepperSteps.length <= 1) {
      return null;
    }
    return (
      <div className="form-stepper-wrapper">
        <Stepper
          steps={stepperSteps}
          activeStep={currentStep}
          maximalStepReached={maxStepReached}
          activeColor={STEPPER_CONFIG.activeColor}
          completeColor={STEPPER_CONFIG.completeColor}
          circleFontSize={STEPPER_CONFIG.circleFontSize}
          titleFontSize={STEPPER_CONFIG.titleFontSize}
          onClick={handleStepChange}
        />
      </div>
    );
  }, [isMultiStep, stepperSteps, currentStep, maxStepReached, handleStepChange]);

  // Custom component props
  const customComponentProps = useMemo(() => ({
    currentStep,
    totalSteps: currentFields.length,
    goToNextStep,
    goToPreviousStep,
    goToStep: handleStepChange,
    resetStepperAndGoToFirstStep,
    formData,
    onChange: onChangeField,
    setFormData,
    validate,
    language,
    match: props.match,
    codeClient: props.codeClient ?? ''
  }), [currentStep, currentFields.length, goToNextStep, goToPreviousStep, handleStepChange, resetStepperAndGoToFirstStep, formData, onChangeField, setFormData, validate, language, props.match, props.codeClient]);

  const formClassName = useMemo(() => {
    const classes = ['formulaire-dynamique'];
    if (sectionsHorizontales) classes.push('formulaire-sections-horizontales');
    if (isMultiStep) classes.push('formulaire-multi-step');
    return classes.join(' ');
  }, [sectionsHorizontales, isMultiStep]);

  // Helper function to render section fields (with or without columns)
  const renderSectionFields = useCallback((section) => {
    const numColumns = section.colonnes || 1;

    if (numColumns === 1) {
      return section.fields.map((field) => (
        <div key={field.idFormulaireDetail} data-field-id={field.idFormulaireDetail}>
          {renderField(field)}
        </div>
      ));
    }

    const fieldsPerColumn = Math.ceil(section.fields.length / numColumns);
    const columns = [];
    for (let i = 0; i < numColumns; i++) {
      columns.push(section.fields.slice(i * fieldsPerColumn, (i + 1) * fieldsPerColumn));
    }

    return (
      <>
        {columns.map((columnFields, colIndex) => (
          <div key={`column-${colIndex}`} className="form-section-column">
            {columnFields.map((field) => (
              <div key={field.idFormulaireDetail} data-field-id={field.idFormulaireDetail}>
                {renderField(field)}
              </div>
            ))}
          </div>
        ))}
      </>
    );
  }, [renderField]);

  if (!fields || fields.length === 0) {
    return (
      <div className="formulaire-dynamique-main-div">
        <span className="formulaire-dynamique-span">Aucun champ à afficher</span>
      </div>
    );
  }

  if (isMultiStep && currentFields[currentStep]?.mockStep) {
    return (
      <div className={formClassName}>
        {renderStepper}
        <div className="formulaire-dynamique-main-div" />
      </div>
    );
  }

  if (isMultiStep && currentFields[currentStep]?.customComponent) {
    const CustomComponent = currentFields[currentStep].customComponent;
    return (
      <div className={formClassName}>
        {renderStepper}
        <CustomComponent {...customComponentProps} />
      </div>
    );
  }

  const { mainTitle, sections } = groupFieldsIntoSections;

  const numColonnesSections = nombreColonnesSections || (sectionsHorizontales ? 4 : 1);

  return (
    <div
      className={formClassName}
      data-sections-columns={sectionsHorizontales ? numColonnesSections : undefined}
    >
      {renderStepper}

      {mainTitle && (
        <div className="form-main-title">
          {renderField(mainTitle)}
        </div>
      )}
      <div
        className={sectionsHorizontales ? 'form-sections-container' : ''}
        style={sectionsHorizontales ? { '--num-sections-columns': numColonnesSections } : {}}
      >
        {sections.map((section, sectionIndex) => {
          const numColumns = section.colonnes || 1;
          return (
            <div
              key={`section-${sectionIndex}`}
              className={`form-section ${section.pleineLargeur ? 'form-section-full-width' : ''}`}
            >
              {section.header && renderField(section.header)}
              <div
                className={`form-section-fields ${numColumns > 1 ? 'form-section-columns' : ''}`}
                style={numColumns > 1 ? { gridTemplateColumns: `repeat(${numColumns}, 1fr)` } : {}}
              >
                {renderSectionFields(section)}
              </div>
            </div>
          );
        })}
      </div>

      {/* <FormTestButton
        onFill={handleFillAllRequiredFields}
        language={language}
        className="formulaire-dynamique-test-button"
      /> */}

      {isMultiStep && (
        <div className="form-step-navigation">
          {currentStep > 0 && (
            <button
              type="button"
              className="form-step-button form-step-button-previous"
              onClick={goToPreviousStep}
            >
              Précédent
            </button>
          )}
          {currentStep < currentFields.length - 1 && (
            <button
              type="button"
              className="form-step-button form-step-button-next"
              onClick={goToNextStep}
            >
              Suivant
            </button>
          )}
          {/* {currentStep === currentFields.length - 1 && (
            <button
              type="button"
              className="form-step-button form-step-button-submit"
              onClick={() => {
                const validation = validate();
                if (validation.isValid && onChange) {
                  onChange(formData);
                }
              }}
            >
              SOUMETTRE
            </button>
          )} */}
        </div>
      )}
    </div>
  );
});

FormulaireDynamique.displayName = 'FormulaireDynamique';

export default FormulaireDynamique;